/**
 * Validation for applicant DD/MM/YYYY and MM/YYYY fields (local calendar, no UTC shift).
 */

export type DatePartsErrorKey =
  | "invalidMonth"
  | "invalidDay"
  | "invalidDate"
  | "futureDate"
  | "pastExpiryDate"
  | "yearOutOfRange";

export interface ValidateDatePartsOptions {
  /** Date must not be after today (start of local day). */
  disallowFuture?: boolean;
  /** Date must be today or in the future (start of local day). */
  requireFutureOrToday?: boolean;
  /** When true, do not enforce minYear/maxYear (any 4-digit year that forms a valid calendar date). */
  skipYearRange?: boolean;
  minYear?: number;
  maxYear?: number;
  /**
   * Day and month are 1–2 digits only (01–31 / 01–12 style or single-digit 1–9).
   * Rejects non-numeric or empty month/day once the field is non-empty.
   */
  flexDayMonthDigits?: boolean;
}

/** Spain arrival step: year 1930–2026, no future, flexible D/M digits. */
export const ARRIVAL_DATE_VALIDATION: ValidateDatePartsOptions = {
  disallowFuture: true,
  skipYearRange: false,
  minYear: 1930,
  maxYear: 2026,
  flexDayMonthDigits: true,
};

export function daysInMonth(month: number, year: number): number {
  if (month < 1 || month > 12) return 31;
  return new Date(year, month, 0).getDate();
}

/** When month has 2 digits, it must be 01–12. */
export function validateMonthString(monthStr: string): DatePartsErrorKey | null {
  if (monthStr.length !== 2) return null;
  const mi = parseInt(monthStr, 10);
  if (Number.isNaN(mi) || mi < 1 || mi > 12) return "invalidMonth";
  return null;
}

/** Month 1–2 digits, numeric, value 1–12 (leading zero optional). */
export function validateMonthStringFlexible(monthStr: string): DatePartsErrorKey | null {
  const m = String(monthStr ?? "").trim();
  if (!m) return null;
  if (!/^\d{1,2}$/.test(m) || m.length > 2) return "invalidMonth";
  const mi = parseInt(m, 10);
  if (Number.isNaN(mi) || mi < 1 || mi > 12) return "invalidMonth";
  return null;
}

/** Day 1–2 digits, numeric (calendar 1–dim checked later with month/year). */
export function validateDayStringFlexible(dayStr: string): DatePartsErrorKey | null {
  const d = String(dayStr ?? "").trim();
  if (!d) return null;
  if (!/^\d{1,2}$/.test(d) || d.length > 2) return "invalidDay";
  return null;
}

/**
 * Full D/M/Y validation when year has 4 digits. Returns null if year incomplete (no error yet)
 * or if all parts empty.
 */
export function validateDatePartsStrings(
  dayStr: string,
  monthStr: string,
  yearStr: string,
  options: ValidateDatePartsOptions = {}
): DatePartsErrorKey | null {
  const d = String(dayStr ?? "").trim();
  const m = String(monthStr ?? "").trim();
  const y = String(yearStr ?? "").trim();

  if (!d && !m && !y) return null;
  if (y.length !== 4) return null;

  if (options.flexDayMonthDigits) {
    const dmErr = validateMonthStringFlexible(m);
    if (dmErr) return dmErr;
    const ddErr = validateDayStringFlexible(d);
    if (ddErr) return ddErr;
  } else if (m.length === 2) {
    const monthErr = validateMonthString(m);
    if (monthErr) return monthErr;
  }

  const yi = parseInt(y, 10);
  if (Number.isNaN(yi)) return "invalidDate";

  if (!options.skipYearRange) {
    const minY = options.minYear ?? 1900;
    const maxY = options.maxYear ?? 2100;
    if (yi < minY || yi > maxY) return "yearOutOfRange";
  }

  if (!d || !m) return null;

  const mi = parseInt(m, 10);
  if (Number.isNaN(mi) || mi < 1 || mi > 12) return "invalidMonth";

  const di = parseInt(d, 10);
  if (Number.isNaN(di)) return "invalidDate";

  const dim = daysInMonth(mi, yi);
  if (di < 1 || di > dim) return "invalidDay";

  const parsed = new Date(yi, mi - 1, di);
  if (parsed.getFullYear() !== yi || parsed.getMonth() !== mi - 1 || parsed.getDate() !== di) {
    return "invalidDate";
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cmp = new Date(yi, mi - 1, di);
  cmp.setHours(0, 0, 0, 0);

  if (options.disallowFuture && cmp > today) return "futureDate";
  if (options.requireFutureOrToday && cmp < today) return "pastExpiryDate";

  return null;
}

/** Single validation pass: month (if 2 chars) + full date when possible. */
export function validateDateValueParts(
  dayStr: string,
  monthStr: string,
  yearStr: string,
  options: ValidateDatePartsOptions = {}
): DatePartsErrorKey | null {
  if (options.flexDayMonthDigits) {
    const mFlex = validateMonthStringFlexible(monthStr);
    if (mFlex) return mFlex;
    const dFlex = validateDayStringFlexible(dayStr);
    if (dFlex) return dFlex;
  } else {
    const mErr = validateMonthString(monthStr.length === 2 ? monthStr : "");
    if (mErr) return mErr;
  }
  return validateDatePartsStrings(dayStr, monthStr, yearStr, options);
}

export function isDatePartsComplete(dayStr: string, monthStr: string, yearStr: string): boolean {
  return (
    String(dayStr ?? "").trim() !== "" &&
    String(monthStr ?? "").trim() !== "" &&
    String(yearStr ?? "").trim().length === 4
  );
}

/** Optional date: empty ok; partial without full year ok (no error); complete must be valid. */
export function validateOptionalDateParts(
  dayStr: string,
  monthStr: string,
  yearStr: string,
  options: ValidateDatePartsOptions
): DatePartsErrorKey | null {
  const d = String(dayStr ?? "").trim();
  const m = String(monthStr ?? "").trim();
  const y = String(yearStr ?? "").trim();
  if (!d && !m && !y) return null;
  if (!isDatePartsComplete(d, m, y)) {
    if (options.flexDayMonthDigits) {
      const mf = validateMonthStringFlexible(m);
      if (mf) return mf;
      const df = validateDayStringFlexible(d);
      if (df) return df;
    } else if (m.length === 2) {
      return validateMonthString(m);
    }
    return null;
  }
  return validateDatePartsStrings(d, m, y, options);
}

export type MonthYearErrorKey = "invalidMonth" | "invalidYear" | "futureMonthYear" | "yearOutOfRange";

export interface ValidateMonthYearOptions {
  /** Month/year must not be after the current calendar month. */
  disallowFuture?: boolean;
  /** When true, do not enforce minYear/maxYear (any 4-digit year with month 01–12). */
  skipYearRange?: boolean;
  minYear?: number;
  maxYear?: number;
}

export function validateMonthYearStrings(
  monthStr: string,
  yearStr: string,
  options: ValidateMonthYearOptions = {}
): MonthYearErrorKey | null {
  const m = String(monthStr ?? "").trim();
  const y = String(yearStr ?? "").trim();
  if (!m && !y) return null;
  if (y.length !== 4) return null;
  if (m.length !== 2) return null;

  const mi = parseInt(m, 10);
  if (Number.isNaN(mi) || mi < 1 || mi > 12) return "invalidMonth";

  const yi = parseInt(y, 10);
  if (Number.isNaN(yi)) return "invalidYear";

  if (!options.skipYearRange) {
    const minY = options.minYear ?? 1900;
    const maxY = options.maxYear ?? 2100;
    if (yi < minY || yi > maxY) return "yearOutOfRange";
  }

  if (options.disallowFuture) {
    const now = new Date();
    const cy = now.getFullYear();
    const cm = now.getMonth() + 1;
    if (yi > cy || (yi === cy && mi > cm)) return "futureMonthYear";
  }

  return null;
}

export function isMonthYearComplete(monthStr: string, yearStr: string): boolean {
  return String(monthStr ?? "").trim().length === 2 && String(yearStr ?? "").trim().length === 4;
}

export function validateOptionalMonthYear(
  monthStr: string,
  yearStr: string,
  options: ValidateMonthYearOptions = {}
): MonthYearErrorKey | null {
  const m = String(monthStr ?? "").trim();
  const y = String(yearStr ?? "").trim();
  if (!m && !y) return null;
  if (!isMonthYearComplete(m, y)) {
    if (m.length === 2) {
      const mi = parseInt(m, 10);
      if (Number.isNaN(mi) || mi < 1 || mi > 12) return "invalidMonth";
    }
    return null;
  }
  return validateMonthYearStrings(m, y, options);
}
